import {
  Box,
  Divider,
  Flex,
  Text,
  Heading,
  Spinner,
  Input,
  Grid,
  GridItem,
  Button,
  HStack,
  useToast,
  Select as SelectBase,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { Header } from '../../components/Header';
import ReactTableComponent from '../../components/TableComponent';
import api from '../../service/api';
import { getColumn } from '../../utils/getOrderColumn';
import { IPeriodo, pedidoNoPeriodo } from '../../helpers/entradaPedido';
import { useHistory } from 'react-router-dom';
import { SiderbarResponsive } from '../../components/SiderbarResponsive';
import { Wapper } from '../../components/Wapper';
import OrderInfoModal from '../../components/OrderInfoModal';
import { useAuth } from '../../context/AuthContext';
import { aprovarPedido, pedidoParaAprovacao } from '../../service/aprovacaoPedido';
import {
  integrarPedidosPendentes,
  podeIntegrarPedidos,
  RECARGAS_APOS_INTEGRAR_MS,
} from '../../service/integracaoPedidos';
import { InputCustom } from '../../components/InputCustom/InputCustom';
import { useForm } from 'react-hook-form';
import { useFiliaisPermitidas } from '../../hooks/useFiliaisPermitidas';
import { listarStatusEntregaPorPedido } from '../../service/entregas';
import { pedidoComErroIntegracao } from '../../service/errosIntegracao';
import { SinoErroIntegracao } from './SinoErroIntegracao';
import { useFiltroDaSessao } from '../../hooks/useFiltroDaSessao';

// Cast para evitar o TS2590 ("union type too complex") do Chakra, como em getOrderColumn.
const Select = SelectBase as React.ComponentType<any>;

export default function ListOrder() {
  const {
    register,
    handleSubmit,
    formState,
    reset,
    control,
    setValue,
    getValues,
  } = useForm();
  const { errors } = formState;
  const [orders, setOrders] = useState<any[]>([]);
  const [showIndex, setShowIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  // O link do pedido recarrega a página: sem guardar na sessão, quem abria um
  // pedido e voltava perdia o período, a filial, a situação e os filtros da tabela.
  const chaveFiltro = (nome: string) =>
    `@Aplication:pedidos:filtro:${user?.id ?? ''}:${nome}`;
  // Período da última pesquisa; null = sem filtro de data.
  const [periodo, setPeriodo] = useFiltroDaSessao<IPeriodo | null>(
    chaveFiltro('periodo'),
    null
  );
  const [initialDate, setInitialDate] = useState(periodo?.inicio ?? '');
  const [finalDate, setFinalDate] = useState(periodo?.fim ?? '');
  const [params, setParams] = useState<any[]>([]);
  const [reload, setReload] = useState(0);
  const [filiais, setFiliais] = useState<{ id: string; nome: string }[]>([]);
  const [filialFiltro, setFilialFiltro] = useFiltroDaSessao(
    chaveFiltro('filial'),
    ''
  );
  const [situacaoFiltro, setSituacaoFiltro] = useFiltroDaSessao(
    chaveFiltro('situacao'),
    ''
  );
  const [integrando, setIntegrando] = useState(false);
  const recargasAposIntegrar = useRef<ReturnType<typeof setTimeout>[]>([]);

  const history = useHistory();
  const {
    ids: filiaisPermitidasIds,
    codigos: filiaisPermitidasCodigos,
    carregando: carregandoFiliais,
  } = useFiliaisPermitidas();

  const toast = useToast();

  // Só gerentes (qualquer variação: regional, nacional, etc.) podem aprovar.
  const isGerente =
    user?.role === 'ROLE_ADMIN' || !!user?.role?.startsWith('ROLE_MANAGER');
  const podeIntegrar = podeIntegrarPedidos(user?.role);

  useEffect(() => {
    if (carregandoFiliais) return;

    async function getParams() {
      try {
        const response = await api.get(
          `/api-essencial/v1/parametrizacao/${user.empresa.id}`
        );

        setValue(
          'percentualAprovacaoPedido',
          response.data[0].percentual_aprovacao_pedido
        );
        setParams(response.data);
      } catch (error) {}
    }
    getParams();

    async function getPedidos() {
      try {
        // O app grava o pedido na filial ATIVA do vendedor (Suplen, NeuroVasc,
        // Suplen BSB...), não na empresa do usuário da web. Buscando só
        // user.empresa.id, o pedido feito em outra filial nunca aparecia para
        // aprovar. Junta os pedidos de todas as empresas do tenant; se a lista
        // de empresas falhar, cai na empresa do usuário como antes.
        let empresaIds: string[] = filiaisPermitidasIds ?? [user.empresa.id];
        const nomePorEmpresa: Record<string, string> = {};
        const codigoPorEmpresa: Record<string, string> = {};
        try {
          // Sem o prefixo /api-essencial/v1: as rotas de empresa são montadas
          // na raiz do ESF-API (app.use(routesEmpresa)). Com o prefixo dá 404.
          const { data: empresas } = await api.get('/empresas');
          const permitidas = filiaisPermitidasIds
            ? new Set(filiaisPermitidasIds)
            : null;
          const ativas = (Array.isArray(empresas) ? empresas : []).filter(
            (e: any) =>
              e?.empresa_id &&
              !e?.excluido &&
              (!permitidas || permitidas.has(e.empresa_id))
          );
          ativas.forEach((e: any) => {
            const nome = e.fantasia || e.razao_social || '';
            nomePorEmpresa[e.empresa_id] = e.filial ? `${e.filial} - ${nome}` : nome;
            codigoPorEmpresa[e.empresa_id] = String(e.filial || '').trim();
          });
          setFiliais(
            ativas
              .map((e: any) => ({ id: e.empresa_id, nome: nomePorEmpresa[e.empresa_id] }))
              .sort((a: any, b: any) => a.nome.localeCompare(b.nome))
          );
          const ids = ativas.map((e: any) => e.empresa_id);
          if (ids.length > 0 || filiaisPermitidasIds) empresaIds = ids;
        } catch {}

        // A entrega (ENTREGUE quando o canhoto foi anexado) não vem no pedido;
        // é o que troca FATURADO por ENTREGUE na coluna Situação. Se o módulo
        // de entregas falhar, a lista sai como antes, só com a situação do ERP.
        const [respostas, entregaPorPedido] = await Promise.all([
          Promise.all(
            empresaIds.map((id) =>
              api
                .get(`/api-essencial/v1/pedidos/${id}/empresa`)
                .then((r) => (Array.isArray(r.data) ? r.data : []))
                .catch(() => [])
            )
          ),
          listarStatusEntregaPorPedido(filiaisPermitidasIds).catch(
            () => ({} as Record<string, string>)
          ),
        ]);
        const vistos = new Set<string>();
        const codigosPermitidos = filiaisPermitidasCodigos
          ? new Set(filiaisPermitidasCodigos)
          : null;
        const response = {
          data: respostas.flat().filter((p: any) => {
            const codigoFilial = String(
              p?.filial_erp || codigoPorEmpresa[p?.empresa_id] || ''
            ).trim();
            if (codigosPermitidos && !codigosPermitidos.has(codigoFilial)) return false;
            if (!p?.pedido_id || vistos.has(p.pedido_id)) return false;
            vistos.add(p.pedido_id);
            p.filial_nome = nomePorEmpresa[p.empresa_id] || '';
            p.status_entrega = entregaPorPedido[p.pedido_id] || '';
            return true;
          }),
        };

        // const orderParams = response.data.data;
        // if (params[0].percentualAprovacaoPedido == undefined) return;
        // const orderParams = response.data.data.filter(
        //   (o: any) => o.margemPedido <= params[0].percentualAprovacaoPedido
        // );

        // Prioridade na lista (mais alta primeiro):
        //   3 = pedido do app aguardando aprovação (EM_ANALISE) — precisa de ação;
        //   2 = qualquer pedido feito pelo app pelos vendedores (tem "payload_enviado"
        //       com a assinatura "INTEGRACAO APP ESF" OU "criado_por_id" preenchido).
        //       O backend nem sempre devolve payload_enviado (vem nulo), então usamos
        //       criado_por_id como sinal adicional — esse campo só existe em pedido do app;
        //   1 = em análise que não veio do app (raro);
        //   0 = demais pedidos (vindos do TOTVS).
        // Dentro de cada grupo, ordena do mais recente para o mais antigo.
        const feitoPeloApp = (o: any) => !!o?.payload_enviado || !!o?.criado_por_id;
        const prioridade = (o: any) => {
          if (feitoPeloApp(o) && pedidoParaAprovacao(o)) return 3;
          if (feitoPeloApp(o)) return 2;
          if (pedidoParaAprovacao(o)) return 1;
          return 0;
        };
        const dataPedido = (o: any) =>
          DateTime.fromISO(o?.ultima_alteracao ?? o?.data_emissao).toMillis();

        const pedidosOrdenados = [...(response.data ?? [])].sort((a: any, b: any) => {
          const pa = prioridade(a);
          const pb = prioridade(b);
          if (pa !== pb) return pb - pa; // maior prioridade primeiro
          return dataPedido(b) - dataPedido(a); // mais recente primeiro
        });

        setOrders(pedidosOrdenados);
      } catch (error) {}

      setIsLoading(false);
    }
    getPedidos();
  }, [orders.length, user.empresa.id, setValue, reload, carregandoFiliais, filiaisPermitidasIds, filiaisPermitidasCodigos]);

  // Recarrega a lista a cada 3 min (mesmo ritmo do Mapa Cirúrgico) para o sino
  // de erro de integração avisar o pedido que chegou com a tela aberta.
  useEffect(() => {
    const intervalo = setInterval(() => setReload((r) => r + 1), 180000);
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    const recargas = recargasAposIntegrar;
    return () => recargas.current.forEach(clearTimeout);
  }, []);

  function showModal(index: number) {
    setShowIndex(index);
  }

  function handleInitialDate(e: any) {
    setInitialDate(e.target?.value);
  }

  function handleFinalDate(e: any) {
    setFinalDate(e.target?.value);
  }

  function handleReset() {
    setInitialDate('');
    setFinalDate('');
    setPeriodo(null);
    setSituacaoFiltro('');
  }

  function handlePercentAprovaded() {
    // if (params[0].percentualAprovacaoPedido == undefined) return;
    // if (initialDate == "" || finalDate == "") {
    //   const orderFilter = orders.filter(
    //     (o: any) => o.margemPedido <= params[0].percentualAprovacaoPedido
    //   );
    // }
  }

  function handleSearch() {
    if (DateTime.fromISO(finalDate) < DateTime.fromISO(initialDate)) {
      return toast({
        title: 'DATAS INVÁLIDAS',
        description: 'A data inicial é maior que a data Final',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }

    // if (
    //   params[0].percentualAprovacaoPedido != "" &&
    //   initialDate === "" &&
    //   initialDate === ""
    // ) {
    //   const orderFilter = orders.filter(
    //     (o) => o.margemPedido <= params[0].percentualAprovacaoPedido
    //   );

    //   setOrderFilter(orderFilter);
    //   return;
    // }

    // A lista do período sai de pedidoNoPeriodo, recalculada a cada recarga
    // (antes era uma cópia tirada na hora da pesquisa e ficava desatualizada).
    setPeriodo(
      initialDate || finalDate ? { inicio: initialDate, fim: finalDate } : null
    );

    // if (orderFilter.length === 0) {
    //   return toast({
    //     title: "SEM PEDIDOS",
    //     description: "Não há pedidos nesse período",
    //     status: "error",
    //     duration: 3000,
    //     isClosable: true,
    //   });
    // }
  }

  async function handleApprove(id: string) {
    try {
      await aprovarPedido(id, user?.name);
      toast({
        title: 'PEDIDO APROVADO',
        description: 'O pedido foi liberado e será integrado ao ERP.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setReload((r) => r + 1);
    } catch (error) {
      toast({
        title: 'ERRO AO APROVAR',
        description: 'Não foi possível aprovar o pedido. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  // Manda agora ao ERP os pedidos liberados que ainda não foram (e os que
  // voltaram com erro), sem esperar a rodada automática de 15 em 15 min.
  async function handleIntegrar() {
    setIntegrando(true);
    try {
      await integrarPedidosPendentes();
      toast({
        title: 'INTEGRAÇÃO INICIADA',
        description:
          'Os pedidos pendentes estão sendo enviados ao ERP. A lista atualiza sozinha em instantes.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      recargasAposIntegrar.current.forEach(clearTimeout);
      recargasAposIntegrar.current = RECARGAS_APOS_INTEGRAR_MS.map((espera) =>
        setTimeout(() => setReload((r) => r + 1), espera)
      );
    } catch (error: any) {
      toast({
        title: 'ERRO AO INTEGRAR',
        description:
          error?.response?.data?.message ||
          'Não foi possível iniciar a integração. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIntegrando(false);
    }
  }

  const pedidosDoPeriodo = periodo
    ? orders.filter((o) => pedidoNoPeriodo(o, periodo))
    : orders;
  const pedidosDaFilial = filialFiltro
    ? pedidosDoPeriodo.filter((o: any) => o.empresa_id === filialFiltro)
    : pedidosDoPeriodo;
  // Os dois grupos que pedem ação: o que espera o gestor aprovar (EM_ANALISE) e
  // o que o TOTVS recusou depois de liberado. São etapas diferentes: pedido com
  // erro não tem o que aprovar, ele é corrigido e reenviado.
  const pedidosParaAprovacao = pedidosDaFilial.filter(pedidoParaAprovacao);
  const pedidosComErro = pedidosDaFilial.filter(pedidoComErroIntegracao);
  const pedidosExibidos =
    situacaoFiltro === 'EM_ANALISE'
      ? pedidosParaAprovacao
      : situacaoFiltro === 'ERRO_INTEGRACAO'
      ? pedidosComErro
      : pedidosDaFilial;

  const column = getColumn(
    () => {},
    '/listar/pedido',
    showModal,
    handleApprove,
    isGerente
  );

  return (
    <>
      <Header />
      <SiderbarResponsive />

      <Flex align="start" mt="8" px="6">
        <Wapper>
          <Box flex="1" p="8" bg="gray.800" borderRadius={8} mb="16">
            <Flex justify="space-between" align="center">
              <Heading size="md" fontWeight="normal">
                LISTA DE PEDIDOS
              </Heading>
              <HStack spacing={4}>
                {podeIntegrar && (
                  <Button
                    size="sm"
                    fontSize="sm"
                    bg="green.500"
                    color="white"
                    _hover={{ bg: 'green.700' }}
                    _focus={{ boxShadow: 'none' }}
                    isLoading={integrando}
                    loadingText="Enviando..."
                    onClick={handleIntegrar}
                    title="Envia agora ao ERP os pedidos pendentes, sem esperar a integração automática"
                  >
                    Integrar pedidos
                  </Button>
                )}
                <SinoErroIntegracao
                  pedidos={orders}
                  pronto={!isLoading}
                  onVerTodos={() => setSituacaoFiltro('ERRO_INTEGRACAO')}
                />
              </HStack>
            </Flex>

            <Divider my="6" borderColor="gray.700" />

            <Grid
              width="100%"
              templateColumns="repeat(5, 1fr) auto"
              gap={4}
              // minChildWidth="120px"
            >
              {/* <Box>
                <InputCustom
                  name="pedido"
                  label="Pedido"
                  errors={errors}
                  register={register}
                />
              </Box> */}
              <Box>
                <InputCustom
                  name="inicio"
                  label="Data Inicial"
                  errors={errors}
                  control={control}
                  value={initialDate}
                  onChange={handleInitialDate}
                  type="date"
                />
              </Box>
              <Box>
                <InputCustom
                  name="final"
                  label="Data Final"
                  errors={errors}
                  control={control}
                  value={finalDate}
                  onChange={handleFinalDate}
                  type="date"
                />
              </Box>
              <Box>
                <InputCustom
                  name="percentualAprovacaoPedido"
                  label="% Aprovação Pedido"
                  errors={errors}
                  register={register}
                  isDisabled
                  // value={percentual_aprovacao_pedido}
                  onChange={handlePercentAprovaded}
                />
              </Box>
              <Box>
                {/* Mesmo FormControl/FormLabel e tamanho padrão do InputCustom,
                    senão o rótulo e a caixa ficam desalinhados das datas. */}
                <FormControl>
                <FormLabel htmlFor="filtro-filial">Filial</FormLabel>
                <Select
                  id="filtro-filial"
                  value={filialFiltro}
                  onChange={(e: any) => setFilialFiltro(e.target.value)}
                  bgColor="gray.700"
                  variant="filled"
                  _hover={{ bgColor: 'gray.700' }}
                >
                  <option value="">Todas as filiais</option>
                  {filiais.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nome}
                    </option>
                  ))}
                </Select>
                </FormControl>
              </Box>
              <Box>
                <FormControl>
                <FormLabel htmlFor="filtro-situacao">Situação</FormLabel>
                <Select
                  id="filtro-situacao"
                  value={situacaoFiltro}
                  onChange={(e: any) => setSituacaoFiltro(e.target.value)}
                  bgColor="gray.700"
                  variant="filled"
                  _hover={{ bgColor: 'gray.700' }}
                >
                  <option value="">Todas as situações</option>
                  <option value="EM_ANALISE">
                    Aguardando aprovação ({pedidosParaAprovacao.length})
                  </option>
                  <option value="ERRO_INTEGRACAO">
                    Só erro de integração ({pedidosComErro.length})
                  </option>
                </Select>
                </FormControl>
              </Box>

              <Flex mt={8} flexDir="row">
                <Button
                  onClick={handleSearch}
                  colorScheme="orange"
                  _focus={{ boxShadow: 'none' }}
                >
                  Pesquisar
                </Button>
                <Button
                  colorScheme="orange"
                  ml={4}
                  onClick={handleReset}
                  _focus={{ boxShadow: 'none' }}
                >
                  Limpar
                </Button>
              </Flex>
            </Grid>

            <Divider my="6" borderColor="gray.700" />

            <Flex justifyContent="center">
              {isLoading ? (
                <Spinner color="white" />
              ) : (
                <>
                  {pedidosExibidos.length === 0 ? (
                    <Flex>
                      <Text color="orange.200">
                        {situacaoFiltro === 'EM_ANALISE'
                          ? 'Nenhum pedido aguardando aprovação'
                          : situacaoFiltro === 'ERRO_INTEGRACAO'
                          ? 'Nenhum pedido com erro de integração'
                          : 'Sem pedidos para exibir'}
                      </Text>
                    </Flex>
                  ) : (
                    <>
                      <ReactTableComponent
                        columns={column}
                        data={pedidosExibidos}
                        isPagenable
                        storageKey={chaveFiltro('tabela')}
                      />

                      <OrderInfoModal
                        order={orders}
                        showIndex={showIndex}
                        setShowIndex={setShowIndex}
                      />
                    </>
                  )}
                </>
              )}
            </Flex>
          </Box>
        </Wapper>
      </Flex>
    </>
  );
}
